import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Eye, EyeOff, Mail, Lock } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { toast } from '../components/Toast'
import { AuthLogo } from '../components/Logo'

export default function LoginPage() {
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState(searchParams.get('email') || '')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [remember, setRemember] = useState(false)
  const [loading, setLoading] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      await login(email, password, remember)
      navigate('/dashboard')
    } catch {
      toast('error', 'Erro ao entrar', 'Email ou senha inválidos')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left - Brand */}
      <div className="hidden lg:flex flex-1 flex-col bg-gradient-to-br from-brand-800 via-brand-900 to-brand-950 items-center justify-center p-12 relative overflow-hidden">
        <div className="relative z-10 flex flex-col items-center text-center max-w-md">
          <h2 className="text-4xl font-bold text-white mb-4">Gestão Política<br/><span className="text-amber-300">Inteligente</span></h2>
          <p className="text-brand-200 text-lg leading-relaxed">Gerencie eleitores, demandas, lideranças e eventos. Da campanha ao mandato.</p>

          <div className="flex gap-8 mt-12">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">100%</p>
              <p className="text-sm text-brand-300">Digital</p>
            </div>
            <div className="w-px bg-white/10" />
            <div className="text-center">
              <p className="text-2xl font-bold text-white">24/7</p>
              <p className="text-sm text-brand-300">Disponível</p>
            </div>
            <div className="w-px bg-white/10" />
            <div className="text-center">
              <p className="text-2xl font-bold text-white">360°</p>
              <p className="text-sm text-brand-300">Visão</p>
            </div>
          </div>
        </div>

        <p className="absolute bottom-6 text-xs text-white/30">Todos os direitos reservados &copy; Sispoli {new Date().getFullYear()}</p>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-[400px] flex-1 flex flex-col justify-center">
          <AuthLogo />

          <h1 className="text-2xl font-bold text-gray-900 mb-2">Bem-vindo de volta</h1>
          <p className="text-gray-500 mb-8">Entre com suas credenciais para acessar</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  className="h-11 w-full pl-11 pr-4 border border-gray-300 rounded-lg text-sm text-gray-800 shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Senha</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-11 w-full pl-11 pr-11 border border-gray-300 rounded-lg text-sm text-gray-800 shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                <span className="text-sm text-gray-600">Lembrar-me</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-brand-500 hover:text-brand-600 font-medium transition-colors">
                Esqueceu a senha?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="h-11 w-full bg-brand-500 hover:bg-brand-600 text-white font-medium rounded-lg text-sm shadow-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Entrando...
                </>
              ) : 'Entrar'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-8">
            Não tem uma conta?{' '}
            <Link to="/register" className="text-brand-500 hover:text-brand-600 font-medium transition-colors">
              Cadastre-se
            </Link>
          </p>
        </div>

      </div>
    </div>
  )
}
